import { HttpClient } from '@angular/common/http';
import {
	AfterViewInit,
	ApplicationRef,
	Component,
	ComponentFactoryResolver,
	ComponentRef,
	ElementRef,
	EventEmitter,
	Injector,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { InterpolacaoCanvasBounds } from '@utils/interpolacao/interpolacao';
import { predict } from '@utils/interpolacao/kriging';
import { FeatureCollection } from 'geojson';
import * as Leaflet from 'leaflet';
import L, {
	latLng,
	latLngBounds,
	LatLngExpression,
	LayerGroup,
	LeafletMouseEvent,
} from 'leaflet';

import { GroupButton } from '@componentes/public-button-group/public-button-group.component';

import { RelatorioVariavelEstacao } from '@home/submodulos/dados-meteorologicos/submodulos/monitoramento/pages/variaveis/interfaces/estacao-ultimas-medicoes';
import { catchError, Subscription } from 'rxjs';
import { circleIcon } from '@utils/leaflet-icons';
import { criarCamadaRotulosMunicipios } from '@utils/leaflet';
import { MAPAS_ESTACOES_AUTOMATICAS, mapEstacoeslayers } from './utils';

@Component({
	selector: 'seira-mapa-interpolacao',
	templateUrl: './mapa-interpolacao.component.html',
	styleUrls: ['./mapa-interpolacao.component.scss'],
})
export class MapaInterpolacaoComponent
	implements AfterViewInit, OnChanges, OnDestroy
{
	static readonly COORDENADAS_PADRAO: LatLngExpression = [-7.2605416, -36.25];
	static readonly ZOOM_PADRAO: number = 8;

	mapa!: L.Map;
	imageOverlay!: L.ImageOverlay;
	@Input()
	interpolacao!: InterpolacaoCanvasBounds;
	@Input()
	imageOpacity = 0;
	marker!: L.Marker;
	@Input()
	mapaId = 'mapa-interpolacao';
	@Input()
	mapaNome = 'Interpolação';
	@ViewChild('mapaDiv')
	mapaDiv!: ElementRef;

	@Input()
	isLegendaOculta = false;
	@ViewChild('legendaMapaMobile') legendaMapaMobile!: TemplateRef<any>;
	@Input() fillOpacity = 0.2;
	@Input() colorJsonInterpolacao = '#414141';
	@Input() weight = 1;

	estacoes: L.Marker[] = [];

	@Input()
	medicoes: RelatorioVariavelEstacao[] = [];
	@Input()
	valores: any;
	@Input()
	carregando = false;
	@Input()
	exportButtons: GroupButton[] = [];
	@ViewChild('actionsTemplate', { static: true, read: TemplateRef })
	actionsTemplate!: TemplateRef<any>;
	@Input()
	unidade: string | undefined = '';
	@Output() mapaEmitter = new EventEmitter<Leaflet.Map>();
	geoJsonLayerSatelite: L.GeoJSON<any> | null = null;
	geoJsonLayerInterpolacao: L.GeoJSON<any> | null = null;
	subscriptions = new Subscription();
	markersLayer: LayerGroup;
	coordenadas: [number, number] = [-7.2605416, -36.7290255];

	options: Leaflet.MapOptions = {
		zoom: 8,
		preferCanvas: true,
		minZoom: 6,
		maxZoom: 19,
		center: latLng(this.coordenadas[0], this.coordenadas[1]),
		maxBounds: latLngBounds(latLng(-4.5, -39.2), latLng(-9.5, -34.2)),
		layers: [mapEstacoeslayers[MAPAS_ESTACOES_AUTOMATICAS.INTERPOLACAO]],
	};

	constructor(
		private httpClient: HttpClient,
		private resolver: ComponentFactoryResolver,
		private appRef: ApplicationRef,
		private injector: Injector
	) {
		this.markersLayer = Leaflet.layerGroup();
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	setupMap(map: L.Map) {
		this.mapa = map;
		this.mapa.attributionControl?.remove();
		this.mapa.createPane('labels');
		this.mapa.getPane('labels')!.style.zIndex = '450';
		this.mapa.getPane('labels')!.style.pointerEvents = 'none';
		this.addResetViewControl(this.mapa);
		this.mapaEmitter.emit(this.mapa);
		this.modifyMapa();
	}

	loadSvg(url: string): Promise<string> {
		// @ts-ignore
		return this.httpClient.get(url, { responseType: 'text' }).toPromise();
	}

	resetMapView() {
		const defaultZoom = 8;
		this.mapa.setView(this.coordenadas, defaultZoom);
	}

	addResetViewControl(map: Leaflet.Map) {
		const resetViewControl = L.Control.extend({
			options: {
				position: 'topleft',
				title: 'Reset view',
			},
			onAdd: (map: Leaflet.Map) => {
				const container = L.DomUtil.create('div', 'leaflet-control-reset');
				container.style.backgroundColor = '#0D6A92';
				container.style.width = '30px';
				container.style.height = '30px';
				container.style.display = 'flex';
				container.style.alignItems = 'center';
				container.style.justifyContent = 'center';
				container.style.cursor = 'pointer';
				container.style.borderBottom = '2px solid transparent';

				this.loadSvg('assets/images/reset_icon.svg').then(svgContent => {
					const svgContainer = L.DomUtil.create('div', '');
					svgContainer.innerHTML = svgContent;
					svgContainer.style.display = 'flex';
					svgContainer.style.alignItems = 'center';
					svgContainer.style.justifyContent = 'center';
					container.innerHTML = '';
					container.appendChild(svgContainer);
				});

				container.onclick = () => {
					this.resetMapView();
				};

				container.onmouseover = () => {
					container.style.backgroundColor = '#3e9536';
				};

				container.onmouseout = () => {
					container.style.backgroundColor = '#0D6A92';
				};

				const existingZoomControls = document.querySelectorAll(
					'.leaflet-control-container .leaflet-top .leaflet-control-zoom a'
				);
				if (existingZoomControls.length === 2) {
					existingZoomControls[1].parentNode!.insertBefore(
						container,
						existingZoomControls[1].nextSibling
					);
				} else {
					const fallbackContainer = L.DomUtil.create(
						'div',
						'leaflet-bar leaflet-control leaflet-control-custom'
					);
					fallbackContainer.appendChild(container);
					return fallbackContainer;
				}

				return L.DomUtil.create('div');
			},
		});

		map.addControl(new resetViewControl());
	}

	modifyMapa() {
		this.subscriptions.add(
			this.loadGeoJson().subscribe(municipios => {
				const rotulos = criarCamadaRotulosMunicipios(municipios);
				const alternarRotulos = () => {
					if (this.mapa.getZoom() > 8) {
						rotulos.addTo(this.mapa);
					} else {
						rotulos.remove();
					}
				};
				this.mapa.on('zoomend', alternarRotulos);
				alternarRotulos();
			})
		);
		this.mapa.addLayer(this.markersLayer);
	}

	// updateMarkers(): void {
	// 	if (this.interpolacao) {
	// 		this.interpolacao.valores.forEach(markerPoint => {
	// 			const marker = new DataMarker<ValoresInterpolacao>(
	// 				[markerPoint.lat, markerPoint.lng],
	// 				{
	// 					icon: this.setIcon(markerPoint),
	// 					data: markerPoint,
	// 				}
	// 			);

	// 			let markerPopup;
	// 			if ('mesoregiao' in markerPoint) {
	// 				markerPopup = this.compilePopup(c => {
	// 					c.instance.markerData = markerPoint;
	// 				}, PopupInformacoesChuvaEstadoComponent);
	// 			} else if (
	// 				'municipio' in markerPoint &&
	// 				!('tipoValor' in markerPoint)
	// 			) {
	// 				markerPopup = this.compilePopup(c => {
	// 					c.instance.markerData = markerPoint;
	// 					c.instance.unidade = this.unidade;
	// 				}, PopupInformacoesPcdComponent);
	// 			} else if ('desvio' in markerPoint) {
	// 				markerPopup = this.compilePopup(c => {
	// 					c.instance.markerData = markerPoint;
	// 				}, PopupMapaDesvioComponent);
	// 			} else if ('tipoValor' in markerPoint) {
	// 				markerPopup = this.compilePopup(c => {
	// 					c.instance.markerData = markerPoint;
	// 				}, PopupPadraoComponent);
	// 			} else {
	// 				if (this.mapaId == 'ndc') {
	// 					markerPopup = this.compilePopup(c => {
	// 						c.instance.markerData = markerPoint;
	// 						c.instance.unidade = this.unidade;
	// 						c.instance.chuvas = this.valores;
	// 					}, PopupInformacoesNdcComponent);
	// 				} else {
	// 					markerPopup = this.compilePopup(c => {
	// 						c.instance.markerData = markerPoint;
	// 					}, PopupInformacoesIapmComponent);
	// 				}
	// 			}

	// 			marker.bindPopup(markerPopup, {
	// 				closeButton: false,
	// 			});
	// 			marker.addTo(this.markersLayer);
	// 		});
	// 	} else {
	// 		const valores: ValoresInterpolacao[] = this.medicoes.map(value => {
	// 			const valorTipoPCD = value.tipoPCD;
	// 			return {
	// 				lat: value.latitude,
	// 				lng: value.longitude,
	// 				value: value.valor,
	// 				municipio: value.municipio,
	// 				valorMax: value.valorMax,
	// 				valorMin: value.valorMin,
	// 				nomePosto: value.nomePosto,
	// 				tipoPCD: this.labelTipoPcd(valorTipoPCD),
	// 			};
	// 		});
	// 		valores.forEach(medicao => {
	// 			const marker = new DataMarker<ValoresInterpolacao>(
	// 				[medicao.lat, medicao.lng],
	// 				{
	// 					icon: this.setIcon(medicao),
	// 					data: medicao,
	// 				}
	// 			);

	// 			let markerPopup;
	// 			marker.bindPopup(markerPopup, {
	// 				closeButton: false,
	// 			});

	// 			marker.addTo(this.markersLayer);
	// 		});
	// 	}

	// 	this.mapa.addLayer(this.markersLayer);
	// }
	ngAfterViewInit(): void {
		this.handleMarkersLabels();
		this.handleInterpolacao();
		this.carregarDadosInterpolacao();
	}

	handleMarkersLabels() {
		this.markersLayer.clearLayers();
		this.mapa.createPane('markers');
		this.mapa.getPane('markers')!.style.zIndex = '1000';
		this.marker = L.marker([0, 0], {
			icon: circleIcon,
			pane: 'markers',
		}).addTo(this.markersLayer);

		this.marker.bindTooltip(L.tooltip({ pane: 'markers' }));

		this.marker.setTooltipContent('-');
	}

	handleInterpolacao() {
		if (this.interpolacao) {
			this.imageOverlay = L.imageOverlay(
				this.interpolacao.canvas.toDataURL(),
				this.interpolacao.latLngBounds,
				{
					opacity: this.imageOpacity,
				}
			);
		}
	}

	// setIcon(estacao: ValoresInterpolacao) {
	// 	return Leaflet.icon({
	// 		iconUrl: `assets/images/leaflet-markers/${this.setIconName(
	// 			'tipoPCD' in estacao ? estacao.tipoPCD : null
	// 		)}.png`,
	// 		iconSize: [7, 7],
	// 	});
	// }

	// setIconName(tipoPCD?: string | null) {
	// 	if (tipoPCD === 'Agrometeorológica') {
	// 		return 'agrometeorologica';
	// 	} else if (tipoPCD === 'Meteorológica') {
	// 		return 'meteorologica';
	// 	}
	// 	return 'full-moon';
	// }

	labelTipoPcd(tipo: string | null) {
		if (tipo === 'METEOROLOGICA') {
			return 'Meteorológica';
		} else if (tipo === 'AGROMETEOROLOGICA') {
			return 'Agrometeorológica';
		}
		return null;
	}

	ngOnChanges(): void {
		this.carregarDadosInterpolacao();
	}

	loadGeoJson = () => {
		return this.httpClient
			.get<FeatureCollection>('assets/geoJson/pb-municipios-geo.json')
			.pipe(
				catchError(error => {
					console.error('Error loading GeoJSON:', error);
					throw error;
				})
			);
	};

	carregarJsonInterpolacao() {
		this.subscriptions.add(
			this.loadGeoJson().subscribe(data => {
				const color = this.colorJsonInterpolacao;
				const weight = this.weight;
				this.geoJsonLayerInterpolacao = L.geoJSON(data, {
					style() {
						return {
							color: color,
							opacity: 0.6,
							weight: weight,
							fillOpacity: 0,
						};
					},
				})
					.addTo(this.mapa)
					.on('click', (event: LeafletMouseEvent) => {
						this.marker.setLatLng(event.latlng);
						if (this.interpolacao) {
							let valor;
							if (
								this.interpolacao.valorConstante &&
								typeof this.interpolacao.valorConstante === 'number'
							) {
								valor = this.interpolacao.valorConstante;
							} else {
								valor = predict(
									event.latlng.lng,
									event.latlng.lat,
									this.interpolacao.variograma
								);
							}

							if (!isNaN(valor)) {
								this.marker.setTooltipContent(
									' Valor : ' +
										(valor < this.interpolacao.legenda[0].valorMin
											? this.interpolacao.legenda[0].valorMin
											: valor
										).toFixed(1) +
										' ' +
										this.interpolacao.legenda[0].unidade +
										''
								);
							}
						}
					});
			})
		);
	}

	carregarDadosInterpolacao() {
		if (this.mapa && this.interpolacao && this.interpolacao.canvas) {
			this.geoJsonLayerSatelite?.remove();
			this.geoJsonLayerSatelite = null;

			if (!this.geoJsonLayerInterpolacao) {
				this.carregarJsonInterpolacao();
			}

			if (!this.imageOverlay) {
				this.imageOverlay = L.imageOverlay(
					this.interpolacao.canvas.toDataURL(),
					this.interpolacao.latLngBounds,
					{
						opacity: this.imageOpacity,
					}
				);
			}
			this.imageOverlay.setOpacity(this.imageOpacity);
			this.imageOverlay.setBounds(this.interpolacao.latLngBounds);
			this.imageOverlay.setUrl(this.interpolacao.canvas.toDataURL());
			this.imageOverlay.addTo(this.mapa);
			//this.updateMarkers();
		} else {
			if (this.imageOverlay) {
				this.imageOverlay.removeFrom(this.mapa);
				this.geoJsonLayerInterpolacao?.remove();
				this.geoJsonLayerInterpolacao = null;

				if (!this.geoJsonLayerSatelite) {
					this.loadGeoJson().subscribe(data => {
						this.geoJsonLayerSatelite = L.geoJSON(data, {
							style() {
								return {
									color: '#FFF',
									opacity: 0.6,
									weight: 1,
									fillOpacity: 0,
								};
							},
						}).addTo(this.mapa);
					});
				}
				//this.updateMarkers();
			}
		}
	}
	compilePopup(onAttach: (el: ComponentRef<any>) => void, component: any) {
		const compFactory = this.resolver.resolveComponentFactory(component);
		const compRef: ComponentRef<any> = compFactory.create(this.injector);

		if (onAttach) onAttach(compRef);

		this.appRef.attachView(compRef.hostView);
		compRef.onDestroy(() => this.appRef.detachView(compRef.hostView));

		const div = document.createElement('div');

		div.appendChild(compRef.location.nativeElement);
		return div;
	}
}
